import { Miscellaneous } from "../model/Miscellaneous";

import { get, post, put, remove } from "../../core/api";
import { EstateCardViewModeType } from "../components/EstateCard/EstateCard";
import { Distribution, EstateFlowchartState, FiduciarySummary, RevocableTrust } from "../model/EstateFlowchartTypes";
import { IrrevocableTrust } from "../model/IrrevocableTrust";
import { headers } from "../utils/TestUtils";

const isSuccess: (response: Response) => boolean = ({status}) => status === 200 || status === 201;

const extractBody = (res: Response) => {
    return res.json()
};

export interface ResponseStatus {
    traceId: string;
    isSuccessStatusCode: boolean;
    statusCode?: number;
    data: any;
}

export const createResponse = (obj: any, status: number) => {
    return {
        headers,
        status,
        json: () => obj
    } as Response;
}

export async function mapResponseStatus(response: Response, statusCodeValidator?: (response: Response) => boolean) {
    let data: any = null;
    const contentType = response.headers?.get('content-type');
    if (contentType && contentType.indexOf('application/json') !== -1) {
        data = await response.json();
    }
    const responseStatus: ResponseStatus = {
        traceId: response.headers?.get('trace-id')!,
        isSuccessStatusCode: statusCodeValidator ? statusCodeValidator(response) : false,
        statusCode: response.status,
        data,
    }
    return responseStatus;
}

export function mapMiscellaneousToBackend(misc: Miscellaneous) {
    const {
        visualCardRepresentation,
        name,
        estateToggleType,
        trustId,
        ...rest
    } = misc;

    let estateStatus;
    if (estateToggleType === "In Estate") {
        estateStatus = "IN_ESTATE";
    } else if (estateToggleType === "Out of Estate") {
        estateStatus = "OUT_OF_ESTATE";
    } else {
        estateStatus = estateToggleType;
    }

    const backend: any = {
        ...rest,
        visualTreatment: visualCardRepresentation.toUpperCase(),
        wildcardName: name,
        estateStatus,
        trustId: trustId === "" ? null : trustId
    };
    return backend;
}

// Backend representation of Miscellaneous
export interface BackendMiscellaneous {
    visualTreatment: string;
    wildcardName: string;
    estateStatus: string;
    trustId?: string | null;
    [key: string]: any; // For any additional backend fields
}

export function mapMiscellaneousFromBackend(misc: BackendMiscellaneous): Miscellaneous {
    const {
        visualTreatment,
        wildcardName,
        estateStatus,
        ...rest
    } = misc;

    let estateToggleType;
    if (estateStatus === "IN_ESTATE") {
        estateToggleType = "In Estate";
    } else if (estateStatus === "OUT_OF_ESTATE") {
        estateToggleType = "Out of Estate";
    } else {
        estateToggleType = estateStatus;
    }

    return {
        ...rest,
        visualCardRepresentation: (typeof visualTreatment === 'string' && visualTreatment.length > 0)
            ? visualTreatment.charAt(0).toUpperCase() + visualTreatment.slice(1).toLowerCase()
            : "",
        name: wildcardName,
        estateToggleType,
    } as Miscellaneous;
}

/**
 * Fetches reference data for estate flowchart.
 * @returns {Promise<ResponseStatus>} The API response status and data.
 */
const getReferenceData = async (): Promise<ResponseStatus> => {
    try {
        const response = await get(`/reference`);
        return await mapResponseStatus(response, isSuccess);
    } catch (err: any) {
        throw createApiError(err);
    }
};


/**
 * Fetches member group for a given profile.
 * @param {string} profileId - The profile ID.
 * @returns {Promise<ResponseStatus>} The API response status and data.
 */
export const getMemberGroup = async (profileId: string): Promise<ResponseStatus> => {
    try {
        const response = await get(`/client-management/external/profiles/${profileId}/members`);
        return await mapResponseStatus(response, isSuccess);
    } catch (err: any) {
        throw createApiError(err);
    }
};


/**
 * Fetches estate flowcharts for a given profile and sets viewMode to SIMPLE.
 * Also maps wildcardTrusts using mapMiscellaneousFromBackend for frontend compatibility.
 * @param {string} profileId - The profile ID.
 * @returns {Promise<ResponseStatus>} The API response status and data.
 */
const getFlowcharts = async (profileId: string): Promise<ResponseStatus> => {
    try {
        const response = await get(`/holistic-advice/profiles/${profileId}/estateFlowchart`);
        const json = await response.json();
        const updated = Array.isArray(json)
            ? json.map(flowchart => ({
                ...flowchart,
                viewMode: EstateCardViewModeType.SIMPLE,
                wildcardTrusts: Array.isArray(flowchart.wildcardTrusts)
                    ? flowchart.wildcardTrusts.map(mapMiscellaneousFromBackend)
                    : []
            }))
            : [];

        const blob = new Blob([JSON.stringify(updated)], {type: 'application/json'});
        const init: ResponseInit = {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers
        };
        const fakeResponse = new Response(blob, init);
        if (response.headers?.get('trace-id')) {
            fakeResponse.headers.set('trace-id', response.headers.get('trace-id')!);
        }
        return await mapResponseStatus(fakeResponse, isSuccess);
    } catch (err: any) {
        throw createApiError(err);
    }
};

/**
 * Helper to create a consistent API error object.
 * @param {any} err - The error thrown by fetch or API call.
 * @returns {Error} Formatted error with traceId, status, and message.
 */
export function createApiError(err: any): Error {
    return new Error(JSON.stringify({
        traceId: err?.headers?.get?.('trace-id') ?? undefined,
        isSuccessStatusCode: false,
        statusCode: err?.status ?? 404,
        data: {error: err?.message ?? String(err)}
    }));
}

const createFlowchart = async (profileId: string, requestBody: EstateFlowchartState): Promise<ResponseStatus> => {
    return await post(`/holistic-advice/profiles/${profileId}/estateFlowchart`, requestBody)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
};

const duplicateFlowchart = async (flowchartName: string, profileId: string, flowchartId: string): Promise<ResponseStatus> => {
    try {
        const response = await post(
            `/holistic-advice/profiles/${profileId}/estateFlowchart/${flowchartId}/duplicate?flowchartName=${encodeURIComponent(flowchartName)}`,
            {}
        );
        const status = await mapResponseStatus(response, isSuccess);
        if (status.data && Array.isArray(status.data.wildcardTrusts)) {
            status.data.wildcardTrusts = status.data.wildcardTrusts.map(mapMiscellaneousFromBackend);
        }
        return status;
    } catch (err: any) {
        throw createApiError(err);
    }
}

const deleteFlowchart = async (profileId: string, flowchartId: string): Promise<ResponseStatus> => {
    return await remove(`/holistic-advice/profiles/${profileId}/estateFlowchart/${flowchartId}`)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
}

const createRevocableTrust = async (profileId: string, flowchartId: string, requestBody: RevocableTrust): Promise<ResponseStatus> => {
    return await post(`/holistic-advice/profiles/${profileId}/estateFlowchart/${flowchartId}/revocableTrust`, requestBody)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
}

const editRevocableTrust = async (profileId: string, flowchartId: string, requestBody: RevocableTrust | IrrevocableTrust) => {
    return await put(`/holistic-advice/profiles/${profileId}/estateFlowchart/${flowchartId}/revocableTrust`, requestBody)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
}

const deleteRevocableTrust = async (profileId: string, revTrustId: string) => {
    return await remove(`/holistic-advice/profiles/${profileId}/estateFlowchart/revocableTrust/${revTrustId}`)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
}

const createIrrevocableTrust = async (profileId: string, flowchartId: string, requestBody: IrrevocableTrust): Promise<ResponseStatus> => {
    return await post(`/holistic-advice/profiles/${profileId}/estateFlowchart/${flowchartId}/irrevocableTrust`, requestBody)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
};

const editIrrevocableTrust = async (profileId: string, flowchartId: string, requestBody: RevocableTrust | IrrevocableTrust) => {
    return await put(`/holistic-advice/profiles/${profileId}/estateFlowchart/${flowchartId}/irrevocableTrust`, requestBody)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
}

const deleteIrrevocableTrust = async (profileId: string, irrevTrustId: string) => {
    return await remove(`/holistic-advice/profiles/${profileId}/estateFlowchart/irrevocableTrust/${irrevTrustId}`)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
}

const createWill = async (profileId: string, flowchartId: string, requestBody: any): Promise<ResponseStatus> => {
    return await post(`/holistic-advice/profiles/${profileId}/estateFlowchart/${flowchartId}/will`, requestBody)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
}

const editWill = async (profileId: string, flowchartId: string, requestBody: any): Promise<ResponseStatus> => {
    return await put(`/holistic-advice/profiles/${profileId}/estateFlowchart/${flowchartId}/will`, requestBody)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
}

const deleteWill = async (profileId: string, willId: string) => {
    return await remove(`/holistic-advice/profiles/${profileId}/estateFlowchart/will/${willId}`)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
}

const deleteSavedDistribution = async (profileId: string, distributionToDelete: Distribution) => {
    const distributionTypeQuery = `?distributionType=${distributionToDelete.distributionType}`;
    return await remove(`/holistic-advice/profiles/${profileId}/estateFlowchart/distribution/${distributionToDelete.distributionId}${distributionTypeQuery}`)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
}

const deletePowerOfAppointment = async (profileId: string, powerOfAppointmentId: string) => {
    return await remove(`/holistic-advice/profiles/${profileId}/estateFlowchart/powersOfAppointment/${powerOfAppointmentId}`)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
}

const createFiduciaries = async (profileId: string, requestBody: FiduciarySummary): Promise<FiduciarySummary> => {
    return await post(`/holistic-advice/profiles/${profileId}/fiduciaries`, requestBody)
        .then(extractBody);
}

const getFiduciaries = async (profileId: string): Promise<ResponseStatus> => {
    return await get(`/holistic-advice/profiles/${profileId}/fiduciaries`)
        .then((res) => mapResponseStatus(res, isSuccess))
        .catch((response) => mapResponseStatus(response));
}

const updateFiduciaries = async (profileId: string, requestBody: FiduciarySummary): Promise<FiduciarySummary> => {
    return await put(`/holistic-advice/profiles/${profileId}/fiduciaries`, requestBody)
        .then(extractBody);
}


const createMiscellaneous = async (profileId: string, flowchartId: string, requestBody: Miscellaneous) => {
    const backendBody = mapMiscellaneousToBackend(requestBody);
    return await post(`/holistic-advice/profiles/${profileId}/estateFlowchart/${flowchartId}/wildcardTrust`, backendBody)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
};

const editMiscellaneous = async (profileId: string, flowchartId: string, requestBody: Miscellaneous) => {
    const backendBody = mapMiscellaneousToBackend(requestBody);
    return await put(`/holistic-advice/profiles/${profileId}/estateFlowchart/${flowchartId}/wildcardTrust`, backendBody)
        .then(async (response: Response) => {
            const status = await mapResponseStatus(response, isSuccess);
            if (status.data) {
                status.data = mapMiscellaneousFromBackend(status.data);
            }
            return status;
        })
        .catch((response: Response) => mapResponseStatus(response));
};


// Delete Miscellaneous (wildcard) trust
const deleteMiscellaneous = async (profileId: string, trustId: string) => {
    return await remove(`/holistic-advice/profiles/${profileId}/estateFlowchart/wildcardTrust/${trustId}`)
        .then((response: Response) => mapResponseStatus(response, isSuccess))
        .catch((response: Response) => mapResponseStatus(response));
};

export const estateFlowchartApiClient = {
    getFlowcharts,
    getMemberGroup,
    getReferenceData,
    createFlowchart,
    duplicateFlowchart,
    deleteFlowchart,
    createRevocableTrust,
    editRevocableTrust,
    deleteRevocableTrust,
    createIrrevocableTrust,
    editIrrevocableTrust,
    deleteIrrevocableTrust,
    createWill,
    editWill,
    deleteWill,
    deleteSavedDistribution,
    deletePowerOfAppointment,
    createFiduciaries,
    getFiduciaries,
    updateFiduciaries,
    createMiscellaneous,
    editMiscellaneous,
    deleteMiscellaneous
}